import styled from '@emotion/styled'
import React, { type FunctionComponent } from 'react'
import { spacing, colors } from '@retailer-platform/shared-components'
import { type Styles } from 'react-select'
import { useEffect } from 'react'
import { RetailerLocaleSelect } from 'gin-and-tonic/containers/locale-select/LocaleSelect'
import { ProductsSearchHeader } from 'domains/products/components/search/components/header/ProductsSearchHeader'
import { ProductsSearchSearchBox } from 'domains/products/components/search/components/search-box/ProductsSearchSearchBox'
import { ProductsSearchDepartmentsAndAislesTree } from 'domains/products/components/search/components/departments-and-aisles-tree/ProductsSearchDepartmentsAndAislesTree'
import { useGetRetailerProductsMesh } from 'domains/products/api/getRetailerProductsMesh.hooks'
import { ProductsSearchTable } from 'domains/products/components/search/components/table/ProductsSearchTable'
import { ProductsYesNoSelector } from 'domains/products/components/yes-no-selector/ProductsYesNoSelector'
import { useProductsSearchListColumns } from 'domains/products/components/search/utils/useProductsSearchListColumns.hooks'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { useSearchParams } from 'domains/products/components/search/utils/useSearchParams.hooks'
import { usePartnerId, useRetailerId } from 'utils/routing/params.hooks'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { ProductsSearchErrorPage } from 'domains/products/components/search/components/error-page/ProductsSearchErrorPage'
import { ProductsDateRangePicker } from 'domains/products/components/date-range-picker/ProductsDateRangePicker'
import { ProductsSearchTypeSelect } from 'domains/products/components/search-type-select/ProductsSearchTypeSelect'
import {
  SearchError,
  useSearchHints,
} from 'domains/products/components/search/productSearch.constants'
import { useDomainAccessControl } from 'domains/products/utils/domain/accessControl'
import { Flipper } from 'domains/products/access-control/flippers'
import { useSelectionManager } from 'utils/selection-manager/SelectionManager'
import { productsViewInAssortmentAccessControl } from 'domains/products/access-control/viewAccess.configuration'
import { ProductsSearchBulkEditButton } from './components/bulk-edit-button/ProductsSearchBulkEditButton'
import { ProductsDownloadButton } from './components/download-button/ProductsDownloadButton'
import ProductsSearchReportAProblem from './components/report-a-problem/ProductsSearchReportAProblem'
import { ProductsSearchBulkEditInAssortmentButton } from './components/bulk-edit-button/ProductsSearchBulkInAssortmentEditButton'

interface ProductsSearchProps {}

const PageLayout = styled.div({
  display: 'grid',
  gridTemplateRows: '[header] 48px [search] 48px [filters] 32px [main] 1fr',
  gridTemplateColumns: '[sidebar] 250px [main] 1fr',
  gridTemplateAreas: `
    "header header"
    "search-type search-bar"
    "sidebar filters"
    "sidebar table"`,
  overflow: 'hidden',
  gridRowGap: spacing.X20,
  height: '100%',
})

const PageHeaderContainer = styled.div({
  gridArea: 'header',
})

const SearchTypeContainer = styled.div({
  gridArea: 'search-type',
  paddingTop: spacing.X4,
  paddingLeft: spacing.X20,
  paddingRight: spacing.X20,
})

const SearchBarContainer = styled.div({
  gridArea: 'search-bar',
  paddingTop: spacing.X4,
  paddingRight: spacing.X20,
})

const ParamContainer = styled.div({
  minWidth: 150,
  marginRight: 12,
})

const ClearAllButton = styled.button({
  background: 'none',
  color: colors.PRIMARY.REGULAR,
  fontWeight: 'bold',
  marginTop: 5,
})

const SidebarContainer = styled.div({
  gridArea: 'sidebar',
  paddingTop: spacing.X8,
  paddingLeft: spacing.X20,
  paddingRight: spacing.X20,
})

const SearchFiltersContainer = styled.div({
  gridArea: 'filters',
  display: 'flex',
  paddingRight: spacing.X20,
})

const TableContainer = styled.div({
  gridArea: 'table',
  paddingRight: spacing.X20,
  overflow: 'hidden',
})

export const ProductsSearch: FunctionComponent<ProductsSearchProps> = () => {
  // imported hooks
  const { notifyError } = useNotifications()

  const retailerId = useRetailerId()
  const partnerId = usePartnerId()
  const { warehouse } = useWarehouseContext()
  const { searchParams, updateSearchParams, setPage, clearSearchParams } = useSearchParams({
    defaultState: {
      retailerId,
      partnerId,
      page: 1,
      locale: warehouse.defaultLocale.localeCode,
      searchTerm: '',
      searchType: 'name',
      limit: 20,
    },
  })
  const hasAccess = useDomainAccessControl()
  const showInAssortment = hasAccess(productsViewInAssortmentAccessControl)

  const {
    data,
    totalCount,
    retailerProductsLoading,
    retailerProductsError,
    itemsLoading,
    refetchItemsData,
    itemsError,
    refetch,
    totalPages,
    searchError,
  } = useGetRetailerProductsMesh(searchParams)
  const columns = useProductsSearchListColumns({
    itemsLoading,
    itemsError: Boolean(itemsError),
    localeCode: searchParams.locale,
    refetchItemsData,
    showInAssortment,
  })

  const { selectedCount, setGlobalCount } = useSelectionManager()
  const i18n = useDomainMessages({
    productCreationDate: 'productsDomain.search.filters.productCreationDate',
    productCreated: 'productsDomain.search.filters.productCreated',
    isPrivateLabelProduct: 'productsDomain.search.filters.isPrivateLabelQuestion',
    hasPrimaryImage: 'productsDomain.search.filters.hasPrimaryImageQuestion',
    inAssortment: 'productsDomain.search.filters.inAssortment',
    clearAll: 'productsDomain.search.clearAll',
    successNotificationHeader: 'productsDomain.search.edit.successNotification.header',
    errorNotificationHeader: 'productsDomain.search.edit.errorNotification.header',
    errorNotificationContent: 'productsDomain.search.edit.errorNotification.content',
    tooManySearchTermNotification: 'productsDomain.search.error.tooManyTerms',
    invalidProductId: 'productsDomain.search.error.invalidProductId',
    emptyStateMessageUpcTip: 'productsDomain.search.makeSearch.upc.tip',
    emptyStateMessageSkuTip: 'productsDomain.search.makeSearch.sku.tip',
  })

  useEffect(() => {
    if (searchError) {
      let errorMessage: string
      switch (searchError) {
        case SearchError.TooManySearchTerms: {
          errorMessage = i18n.tooManySearchTermNotification
          break
        }
        case SearchError.InvalidProductId: {
          errorMessage = i18n.invalidProductId
          break
        }
      }
      // dismiss after 15 seconds
      notifyError(errorMessage, 'modern', 15000)
    }
  }, [searchError, i18n, notifyError])

  let emptyStateMessageTip = null
  if (searchParams.searchType == 'upc') emptyStateMessageTip = i18n.emptyStateMessageUpcTip
  else if (searchParams.searchType == 'rrc') emptyStateMessageTip = i18n.emptyStateMessageSkuTip

  const searchHints = useSearchHints()

  const yesNoStyles: Styles = {
    container: () => ({
      minWidth: 200,
    }),
  }

  const showLocaleSelect = warehouse.locales.length > 1

  useEffect(() => {
    setGlobalCount(totalCount)
  }, [totalCount, setGlobalCount])

  const showSelectionButtons = selectedCount > 0

  return (
    <PageLayout>
      <PageHeaderContainer>
        <ProductsSearchHeader />
      </PageHeaderContainer>
      <SearchTypeContainer>
        <ProductsSearchTypeSelect
          isModernTheme
          value={searchParams.searchType}
          onChange={searchType => {
            updateSearchParams({ searchType })
          }}
        />
      </SearchTypeContainer>
      <SearchBarContainer>
        <ProductsSearchSearchBox
          id="products-search-search-box"
          value={searchParams.searchTerm}
          placeholder={searchHints[searchParams.searchType]}
          onChange={searchTerm => updateSearchParams({ searchTerm })}
        />
      </SearchBarContainer>
      <SidebarContainer>
        <ProductsSearchDepartmentsAndAislesTree
          retailerId={retailerId}
          departmentId={searchParams.departmentId}
          aisleId={searchParams.aisleId}
          onClickItem={props => {
            const [departmentId, aisleId] = props.key.split('/')

            updateSearchParams({
              departmentId: departmentId || undefined,
              aisleId: aisleId || undefined,
            })
          }}
        />
      </SidebarContainer>
      <SearchFiltersContainer>
        <ProductsDateRangePicker
          dateRangeOption={searchParams.creationDateRange}
          customStartDate={searchParams.creationDateCustomStart}
          customEndDate={searchParams.creationDateCustomEnd}
          onDateRangeChanged={(newRange, customStart, customEnd) => {
            updateSearchParams({
              creationDateRange: newRange,
              creationDateCustomStart: customStart,
              creationDateCustomEnd: customEnd,
            })
          }}
          placeholder={`${i18n.productCreationDate}...`}
          itemDisplayPrefix={i18n.productCreated}
        />

        <ParamContainer>
          <ProductsYesNoSelector
            value={searchParams.isStoreBrandProduct}
            onChange={isStoreBrandProduct =>
              updateSearchParams({ isStoreBrandProduct: isStoreBrandProduct })
            }
            isModernTheme
            styles={yesNoStyles}
            placeholder={i18n.isPrivateLabelProduct}
          />
        </ParamContainer>

        <ParamContainer>
          <ProductsYesNoSelector
            value={searchParams.hasPrimaryImageUrl}
            onChange={hasPrimaryImageUrl =>
              updateSearchParams({ hasPrimaryImageUrl: hasPrimaryImageUrl })
            }
            isModernTheme
            styles={yesNoStyles}
            placeholder={i18n.hasPrimaryImage}
          />
        </ParamContainer>

        {showLocaleSelect && (
          <ParamContainer>
            <RetailerLocaleSelect
              value={searchParams.locale}
              onChange={locale => updateSearchParams({ locale: locale.localeCode })}
              isModernTheme
            />
          </ParamContainer>
        )}

        <ParamContainer>
          <ClearAllButton onClick={clearSearchParams}>{i18n.clearAll}</ClearAllButton>
        </ParamContainer>

        <div css={{ flex: 1 }} />

        {showSelectionButtons && (
          <React.Fragment>
            <ProductsSearchReportAProblem searchParams={searchParams} />
            <ProductsSearchBulkEditButton searchParams={searchParams} />
            {showInAssortment && (
              <ProductsSearchBulkEditInAssortmentButton searchParams={searchParams} />
            )}
          </React.Fragment>
        )}

        {hasAccess({ warehouseFlippers: Flipper.ProductsDownload }) && (
          <ProductsDownloadButton searchParams={searchParams} />
        )}
      </SearchFiltersContainer>
      <TableContainer>
        <ProductsSearchTable
          columns={columns}
          data={data}
          isLoading={retailerProductsLoading}
          totalPages={totalPages}
          page={searchParams.page}
          onPageChange={setPage}
          emptyStateMessageTip={emptyStateMessageTip}
          errorMessage={
            retailerProductsError && <ProductsSearchErrorPage action={() => refetch()} />
          }
          disablePagination={Boolean(retailerProductsError || !data)}
          itemsPerPage={searchParams.limit}
          onItemsPerPageChange={limit => updateSearchParams({ limit })}
        />
      </TableContainer>
    </PageLayout>
  )
}
